import { Zap, TriangleAlert as AlertTriangle, Lightbulb, MessageCircle } from 'lucide-react';
import type { BdrBattlecard } from '../../../types';

interface Props {
  battlecard: BdrBattlecard;
  compact?: boolean;
}

function SectionHeader({ icon: Icon, label }: { icon: typeof Zap; label: string }) {
  return (
    <div className="flex items-center gap-1.5 mb-1.5">
      <Icon className="w-3.5 h-3.5 text-forest-600" />
      <span className="text-[11px] font-bold text-forest-700 uppercase tracking-wider">{label}</span>
    </div>
  );
}

function BulletList({ items }: { items: string[] }) {
  return (
    <ul className="space-y-1">
      {items.map((item, i) => (
        <li key={i} className="flex items-start gap-1.5">
          <div className="w-1 h-1 rounded-full bg-forest-500 mt-[7px] flex-shrink-0" />
          <span className="text-xs text-app-ink leading-relaxed">{item}</span>
        </li>
      ))}
    </ul>
  );
}

export default function BdrBattlecardSection({ battlecard, compact = false }: Props) {
  const hasContent =
    battlecard.trigger_context?.length > 0 ||
    battlecard.problem_impact?.length > 0 ||
    battlecard.solution_pov?.length > 0 ||
    battlecard.talk_track_hooks?.opener;

  if (!hasContent) return null;

  return (
    <div className="rounded-lg border border-forest-200 bg-forest-50/60 overflow-hidden">
      <div className="px-4 py-2.5 bg-forest-100/70 border-b border-forest-200">
        <span className="text-[11px] font-bold text-forest-800 uppercase tracking-widest">BDR Battlecard</span>
      </div>

      <div className={`px-4 py-3 ${compact ? 'space-y-3' : 'grid grid-cols-1 sm:grid-cols-2 gap-4'}`}>
        {battlecard.trigger_context?.length > 0 && (
          <div>
            <SectionHeader icon={Zap} label="Trigger + Context" />
            <BulletList items={battlecard.trigger_context} />
          </div>
        )}

        {battlecard.problem_impact?.length > 0 && (
          <div>
            <SectionHeader icon={AlertTriangle} label="Problem + Impact" />
            <BulletList items={battlecard.problem_impact} />
          </div>
        )}

        {battlecard.solution_pov?.length > 0 && (
          <div>
            <SectionHeader icon={Lightbulb} label="Solution POV" />
            <BulletList items={battlecard.solution_pov} />
          </div>
        )}

        {battlecard.talk_track_hooks && (battlecard.talk_track_hooks.opener || battlecard.talk_track_hooks.probes?.length > 0) && (
          <div>
            <SectionHeader icon={MessageCircle} label="Talk Track + Hooks" />
            <div className="space-y-1">
              {battlecard.talk_track_hooks.opener && (
                <p className="text-xs text-app-ink font-medium leading-relaxed">
                  <span className="text-forest-700 font-semibold">Opener:</span> {battlecard.talk_track_hooks.opener}
                </p>
              )}
              {battlecard.talk_track_hooks.probes?.length > 0 && (
                <BulletList items={battlecard.talk_track_hooks.probes} />
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
